import { useState } from 'react';
import type { AxiosResponse } from 'axios';

import { getUserFacingApiMessageAsync } from '../utils/apiErrorDetail';
import { downloadResponseAsFile } from '../utils/downloadFile';
import { useToast } from './useToastContext';
import { usePdfTab } from './usePdfTab';

const BILLING_PDF_ERROR_FALLBACK =
  'Unexpected error occurred. Please try again or contact support';

interface PdfResult {
  data?: unknown;
  error?: unknown;
}

/**
 * Fetch a billing document and hand it to the biller, with one error path
 * (a toast naming `failureTitle`). A PDF opens in a new tab: the tab opens
 * inside the click so popup blockers allow it, fills with the fetched blob,
 * and closes when the fetch fails. With `fileName` the response is saved as
 * a file instead, for text such as the 837I EDI, and no tab opens.
 */
export const useBillingPdfDownload = (
  fetchPdf: () => Promise<PdfResult>,
  {
    failureTitle,
    logLabel,
    fileName,
  }: { failureTitle: string; logLabel: string; fileName?: string }
) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { error: showError } = useToast();
  const openPdfTab = usePdfTab();

  // The whole result (not just `.error`) goes to the message helper so it can
  // read the HTTP status and suppress 5xx/proxy bodies centrally.
  const showFailure = async (source: unknown) => {
    showError(
      failureTitle,
      await getUserFacingApiMessageAsync(source, BILLING_PDF_ERROR_FALLBACK)
    );
  };

  const download = async () => {
    const tab = fileName ? null : openPdfTab();
    if (!fileName && !tab) return;
    setIsDownloading(true);
    try {
      const resp = await fetchPdf();
      if (resp.error || !resp.data) {
        tab?.close();
        console.error(`Failed to download ${logLabel}:`, resp.error);
        await showFailure(resp);
        return;
      }
      if (fileName) {
        downloadResponseAsFile(resp as unknown as AxiosResponse, fileName);
        return;
      }
      const blob =
        resp.data instanceof Blob
          ? resp.data
          : new Blob([resp.data as BlobPart], { type: 'application/pdf' });
      tab?.setBlob(blob);
    } catch (error) {
      tab?.close();
      console.error(`Failed to download ${logLabel}:`, error);
      await showFailure(error);
    } finally {
      setIsDownloading(false);
    }
  };

  return { isDownloading, download };
};
